import React, { useEffect, useState } from 'react';
import { systemRoles as rolesApi } from '../../api/endpoints';
import { FormLayout, Input, Badge, Button, TablePanel, ConfirmDialog } from '../../components/ui';
import PermissionGrid, { noPermissions, parsePermissions } from './PermissionGrid';
import type { Permissions } from './PermissionGrid';
import type { SystemRole } from '../../types';
import type { Column } from '../../components/ui';
import type { Tokens } from '../../theme/tokens';

// Settings › Users › Roles. Users point at a role by id, so renaming one is
// safe (the server renames it on its users). Built-in roles — System
// Administrator and Sunday School Teacher — cannot be renamed, turned off or
// deleted; a role someone still holds cannot be deleted, only turned off.

interface RolesManagerProps {
  t: Tokens;
  notify: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
  /** Called after any change, so the user form's role list refreshes. */
  onChanged?: () => void;
}

const EMPTY: SystemRole = { name: '', description: '', active: true };

const RolesManager = ({ t, notify, onChanged }: RolesManagerProps) => {
  const [roles, setRoles] = useState<SystemRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<SystemRole | null>(null);
  const [useDefaults, setUseDefaults] = useState(false);
  const [defaults, setDefaults] = useState<Permissions>(noPermissions);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<SystemRole | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const list = await rolesApi.list();
      setRoles(Array.isArray(list) ? list : []);
    } catch (err) {
      notify((err as Error).message || 'Could not load the roles.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const open = (role: SystemRole) => {
    setEditing({ ...role });
    setUseDefaults(Boolean(role.defaultPermissions));
    setDefaults(parsePermissions(role.defaultPermissions));
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    const payload: SystemRole = {
      ...editing,
      name: editing.name.trim(),
      defaultPermissions: useDefaults ? JSON.stringify(defaults) : ''
    };
    setSaving(true);
    try {
      if (editing.id) await rolesApi.update(editing.id, payload);
      else await rolesApi.create(payload);
      notify(editing.id ? 'Role updated.' : 'Role created.', 'success');
      setEditing(null);
      await load();
      onChanged?.();
    } catch (err) {
      notify((err as Error).message || 'Could not save the role.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    const role = deleting;
    setDeleting(null);
    if (!role?.id) return;
    try {
      await rolesApi.remove(role.id);
      notify(`Role "${role.name}" deleted.`, 'success');
      await load();
      onChanged?.();
    } catch (err) {
      notify((err as Error).message || 'Could not delete the role.', 'error');
    }
  };

  const columns: Array<Column<SystemRole>> = [
    {
      key: 'name', header: 'Role',
      render: (r) => (
        <>
          <strong>{r.name}</strong>{' '}
          {r.builtIn && <Badge tone="accent">Built-in</Badge>}
          {r.description && (
            <div style={{ color: t.textMuted, fontSize: t.fontSize.sm, marginTop: '2px' }}>{r.description}</div>
          )}
        </>
      )
    },
    { key: 'userCount', header: 'Users', align: 'right', render: (r) => r.userCount ?? 0 },
    {
      key: 'defaults', header: 'Default permissions',
      render: (r) => (r.defaultPermissions ? 'Set' : <span style={{ color: t.textFaint }}>—</span>)
    },
    {
      key: 'active', header: 'Status',
      render: (r) => (r.active ? <Badge tone="success">Active</Badge> : <Badge tone="warning">Turned off</Badge>)
    },
    {
      key: 'actions', header: 'Actions', align: 'right',
      render: (r) => (
        <div style={{ display: 'flex', gap: '5px', justifyContent: 'flex-end' }}>
          <Button variant="ghost" size="sm" onClick={() => open(r)}>Edit</Button>
          {!r.builtIn && (
            <Button variant="ghost" size="sm" onClick={() => setDeleting(r)}>Delete</Button>
          )}
        </div>
      )
    }
  ];

  if (editing) {
    const builtIn = Boolean(editing.builtIn);
    return (
      <FormLayout
        title={editing.id ? `Edit Role: ${editing.name}` : 'New Role'}
        submitLabel={editing.id ? 'Save Role' : 'Create Role'}
        submitting={saving}
        onSubmit={save}
        onCancel={() => setEditing(null)}
        cancelLabel="Cancel"
        maxWidth="1000px"
      >
        <FormLayout.Grid columns={2}>
          <Input
            label="Role name" required maxLength={60} placeholder="e.g. Treasurer"
            value={editing.name} disabled={builtIn}
            hint={builtIn ? 'Built-in: the server relies on this name.' : 'Renaming also renames it on every user who holds it.'}
            onChange={(e) => setEditing({ ...editing, name: e.target.value })}
          />
          <Input
            label="Description" maxLength={300} placeholder="What this role does"
            value={editing.description || ''}
            onChange={(e) => setEditing({ ...editing, description: e.target.value })}
          />
        </FormLayout.Grid>

        <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', margin: '6px 0 18px', cursor: builtIn ? 'default' : 'pointer' }}>
          <input
            type="checkbox" checked={editing.active} disabled={builtIn}
            onChange={(e) => setEditing({ ...editing, active: e.target.checked })}
            style={{ width: '18px', height: '18px', marginTop: '2px' }}
          />
          <span>
            <strong style={{ color: t.textMain }}>Active</strong>
            <span style={{ display: 'block', color: t.textMuted, fontSize: t.fontSize.sm }}>
              Turned off, the role cannot be given to anyone new; people who hold it keep it.
            </span>
          </span>
        </label>

        <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '14px', cursor: 'pointer' }}>
          <input
            type="checkbox" checked={useDefaults}
            onChange={(e) => setUseDefaults(e.target.checked)}
            style={{ width: '18px', height: '18px', marginTop: '2px' }}
          />
          <span>
            <strong style={{ color: t.textMain }}>Default permissions</strong>
            <span style={{ display: 'block', color: t.textMuted, fontSize: t.fontSize.sm }}>
              Filled in when a new user is given this role. They can still be changed for each user.
            </span>
          </span>
        </label>
        {useDefaults && (
          <PermissionGrid title="Default module access" permissions={defaults} onChange={setDefaults} t={t} />
        )}
      </FormLayout>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '0 0 12px', gap: '10px', flexWrap: 'wrap' }}>
        <p style={{ color: t.textMuted, fontSize: t.fontSize.sm, margin: 0, maxWidth: '720px' }}>
          Every user holds one of these roles. Renaming a role is safe. A role someone still holds
          cannot be deleted — turn it off instead. Approval levels will be assigned to roles.
        </p>
        <Button onClick={() => open(EMPTY)}>+ New Role</Button>
      </div>
      <TablePanel
        title={<>System Roles</>}
        columns={columns}
        rows={roles}
        rowKey="id"
        loading={loading}
        emptyMessage="No roles yet."
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete role"
        message={`Delete the role "${deleting?.name || ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        onConfirm={confirmDelete}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
};

export default RolesManager;
